/** STXReferenceNumber — the SINGLE SOURCE OF TRUTH for SUNTRONIX ITAM
 *  reference / document numbers across every existing and future module
 *  (Quotations, Sales Orders, Purchase Orders, Invoices, Delivery Challans,
 *  Asset Transfers, Deployment, Recovery / Reverse Pickup, Service / Repair,
 *  Warehouse, Redeployment, Buyback, Recycling / Disposal, …).
 *
 *  GUARANTEES (display + copy only — never touches generation/sequencing/DB):
 *   • Verdana · 12pt · Bold  (inline style, cannot be overridden by page typography)
 *   • Always ONE LINE — never wraps (white-space: nowrap)
 *   • Real, SELECTABLE text (never an image / SVG / canvas)
 *   • Exact stored value — no case change, spaces, quotes, brackets, hyphen edits
 *   • Optional Copy icon → copies the EXACT reference only; rich text/html
 *     (Verdana 12pt Bold) + text/plain fallback; subtle inline "Copied" confirm
 *
 *  Usage:
 *    <STXReferenceNumber value="STX-Q-2026-00218" />
 *    <STXReferenceNumber value={order.order_no} copy />
 *    <STXReferenceNumber value={q.code} copy asChildOfRow />  // inside a clickable row
 */
import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { copyReference } from "@/lib/copyReference";

const STX_REF_STYLE = {
  fontFamily: "Verdana, Arial, sans-serif",
  fontSize: "12pt",
  fontWeight: 700,
  whiteSpace: "nowrap",
  userSelect: "text",
  WebkitUserSelect: "text",
};

export function STXReferenceNumber({
  value,
  copy = false,
  size = 14,
  className = "",
  title,
  onClick,
  asChildOfRow = false,
  "data-testid": testId,
}) {
  const [done, setDone] = useState(false);
  const ref = String(value ?? "").trim();   // trims only surrounding whitespace; value itself is untouched
  if (!ref) return null;

  const doCopy = async (e) => {
    // Always isolate the copy action from any surrounding clickable row/link.
    e.stopPropagation();
    e.preventDefault();
    const ok = await copyReference(ref);
    if (ok) {
      setDone(true);
      window.setTimeout(() => setDone(false), 1500);
    }
  };
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") doCopy(e);
  };

  return (
    <span
      className={"stx-ref inline-flex items-center gap-1.5 " + className}
      data-testid={testId || `stx-ref-${ref}`}
      data-reference={ref}
      style={{ whiteSpace: "nowrap" }}
    >
      <span
        style={STX_REF_STYLE}
        title={title || ref}
        onClick={onClick}
        className={"stx-ref-text" + (onClick ? " cursor-pointer" : "")}
      >
        {ref}
      </span>
      {copy && (
        // role=button span (NOT a <button>) so it is valid inside clickable
        // <button>/<a> rows and never causes nested-interactive HTML.
        <span
          role="button"
          tabIndex={0}
          onClick={doCopy}
          onKeyDown={onKey}
          data-testid={`copy-ref-${ref}`}
          aria-label="Copy reference number"
          title="Copy reference (Verdana 12pt Bold)"
          className="inline-flex items-center gap-1 rounded px-0.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-400 transition-colors cursor-pointer select-none"
        >
          {done ? (
            <>
              <Check size={size} className="text-emerald-600" />
              <span className="text-[10px] font-medium text-emerald-600">Copied</span>
            </>
          ) : (
            <Copy size={size} />
          )}
        </span>
      )}
    </span>
  );
}

export default STXReferenceNumber;
